import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import useCountry from 'hooks/useCountry';
import useFeatureFlags from 'hooks/useFeatureFlags';

import Grid from '@material-ui/core/Grid';
import Link from 'components/CncUI/Link';
import Typography from 'components/CncUI/Typography';
import Button from 'components/CncUI/Button';
import Switch from 'components/CncUI/Switch';
import Checkbox from 'components/CncUI/Checkbox';
import Radio from 'components/CncUI/Radio';
import ContentBox from 'components/CncUI/ContentBox';
import ConfirmModal from './ConfirmModal';
import UpdateNamesModal from './UpdateNamesModal';
import DeleteModal from './DeleteModal';
import BankAccounts from './BankAccounts';
import AccountsTable from './AccountsTable';

import shield from 'images/shield.svg';

import EnumHelper from 'utilities/enumHelper';

const MAX_ACCOUNTS = 3;
const MAX_PERCENTAGE = 100;
const FEATURE_FLAG_CURRENCY = 'MyCnC-DirectDeposits-Currency';
const FEATURE_FLAG_LOANOUTS = 'MyCnC-LoanOuts';

const BLANK_DEPOSIT = {
  id: null,
  routingNumber: '',
  bankAccountNumber: '',
  bankName: '',
  isDirectDeposit: true,
  flatAmount: 0,
  percentRate: 0,
  splitPercentage: 0,
  bankAccountType: 0,
  isPaperLess: true,
  isDeliveryRequested: false,
  ba: '',
  rn: '',
};

const useStyles = makeStyles(({ palette }) => ({
  actions: {
    marginTop: 0,
    marginBottom: -16,
  },
  enabled: {
    color: palette.cncUI.default.primary,
  },
  error: {
    color: palette.cncUI.alert.red.primary,
  },
  plaidBox: {
    display: 'flex',
  },
  plaidTitle: {
    color: palette.cncUI.primary.green['-5'],
    margin: 0,
    paddingLeft: 4,
  },
  bold: {
    fontWeight: 500,
  },
  description: {
    color: palette.cncUI.primary.gray['+2'],
    marginTop: -6,
    marginBottom: 16,
  },
  finePrint: {
    color: palette.cncUI.primary.gray['+3'],
  },
}));

const EditForm = props => {
  const classes = useStyles();
  const { country } = useCountry();
  const { isEnabled } = useFeatureFlags();
  const isCanada = country === 'CAN';
  const isCurrencyEnabled = isEnabled(FEATURE_FLAG_CURRENCY);
  const loanoutsEnabled = isEnabled(FEATURE_FLAG_LOANOUTS);
  const { directDeposits = [], mappings, removeRestriction } = props;
  const { onCancel, onSave, enabled, setEnabled, dbCode } = props;
  const { oAuthState } = props;
  const defaultData = {
    ...BLANK_DEPOSIT,
    ...((directDeposits && directDeposits[0]) || {}),
  };
  const [confirm, setConfirm] = useState(false);
  const [updateNames, setUpdateNames] = useState(false);
  const [accountsToUpdate, setAccountsToUpdate] = useState([]);
  const [removing, setRemoving] = useState(null);
  const [paperless, setPaperless] = useState(
    isCanada ? false : defaultData.isPaperLess,
  );
  const [delivery, setDelivery] = useState(defaultData.isDeliveryRequested);
  const [amountType, setAmountType] = useState(
    defaultData.flatAmount ? '$' : '%',
  );
  const initialDirectDeposits = (directDeposits || []).map((deposit, index) => {
    return {
      ...deposit,
      primary: index === (directDeposits || []).length - 1,
    };
  });
  const [form, setForm] = useState(initialDirectDeposits);
  const multiple = true; // sets the splits on direct deposits

  const key = prop => `EditForm-directDeposit-${prop}`;

  const addBankAccounts = accounts => {
    if ((accounts || []).length === 0) {
      return;
    }

    const format = (account, primary = false) => ({
      ...BLANK_DEPOSIT,
      primary,
      currencyType: account.currencyType || 'US',
      routingNumber: account.routing,
      bankName: account.bankName || '',
      bankAccountNumber: account.account,
      bankAccountType: EnumHelper.labelToOptionValue(
        mappings.bankAccountTypes,
        `${account.subtype} Account`,
        false,
      ),
      ba: account.ba,
      rn: account.rn,
      ...(loanoutsEnabled ? { forCorps: 'i' } : {}),
    });

    if (!multiple) {
      setForm([format(accounts[0], true)]);
    } else {
      const filters = (form || []).map(
        d => `${d.routingNumber}:${d.bankAccountNumber}`,
      );
      const bankAccounts = (accounts || [])
        .filter(a => !filters.includes(`${a.routing}:${a.account}`))
        .map(a => format(a));
      const newAccounts = [...form, ...bankAccounts];
      setForm(
        newAccounts.map((account, index) => ({
          ...account,
          primary: index === newAccounts.length - 1,
        })),
      );
    }
  };

  const handleChange = ({ target: { name, value } }, index) => {
    form[index][name] = value;
    setForm([...form]);
  };

  const handleRemove = index => {
    const newAccounts = form.filter((_, i) => i !== index);
    setForm(
      newAccounts.map((a, i) => ({
        ...a,
        primary: i === newAccounts.length - 1,
      })),
    );
    setRemoving(null);
  };

  const getSumPercentages = () => {
    if (form && form.length > 1 && amountType === '%') {
      const value = item => parseInt(item.splitPercentage, 10) || 0;
      const splitAccounts = form.filter(a => !a.primary);
      return splitAccounts.reduce((memo, item) => memo + value(item), 0);
    }
    return 0;
  };

  const valid = () => {
    if (!enabled) return form.length > 0 || initialDirectDeposits.length > 0;
    if (form.length === 0) return false;

    if (!multiple) {
      return form.length > 0;
    }

    const additional = form.filter(a => !a.primary);
    const field = amountType === '%' ? 'splitPercentage' : 'flatAmount';
    const amount = item => parseFloat(item[field]) || 0;
    if (additional && additional.some(item => amount(item) === 0)) {
      return false;
    }

    if (form.length > MAX_ACCOUNTS || getSumPercentages() >= MAX_PERCENTAGE) {
      return false;
    }

    return true;
  };

  const errorMessage = () => {
    if (!enabled || !multiple || form.length === 0) return '';
    const additional = form.filter(a => !a.primary);
    const field = amountType === '%' ? 'splitPercentage' : 'flatAmount';
    const amount = item => parseFloat(item[field]) || 0;
    if (additional && additional.some(item => amount(item) === 0)) {
      return `Each account must receive at least ${
        amountType === '%' ? '1%' : '$1'
      }`;
    }

    if (form.length > MAX_ACCOUNTS || getSumPercentages() >= MAX_PERCENTAGE) {
      return `Split Amount ${
        form.length === 2 ? 'of 1' : 'sum 1 and 2'
      } must be less than 99%`;
    }

    return '';
  };

  const formatDeposit = deposit => {
    const { primary, __typename, ...rest } = deposit;
    const percentage = amountType === '%';
    let splitPercentage = 0;
    if (percentage) {
      splitPercentage = primary
        ? MAX_PERCENTAGE - getSumPercentages()
        : parseInt(deposit.splitPercentage, 10);
    } else {
      splitPercentage = primary ? 100 : 0;
    }
    const formattedDeposit = {
      ...rest,
      currencyType: deposit.currencyType || 'US',
      bankAccountType: parseInt(deposit.bankAccountType, 10),
      isPaperLess: isCanada ? false : paperless,
      isDeliveryRequested: isCanada ? true : delivery,
      flatAmount:
        amountType === '$' && !primary ? parseInt(deposit.flatAmount, 10) : 0,
      splitPercentage: splitPercentage,
      forCorps: 'i',
    };
    if (!loanoutsEnabled) delete formattedDeposit.forCorps;
    return formattedDeposit;
  };

  const shouldUpdateBankNames = () => {
    if (!enabled) {
      return false;
    }
    if (accountsToUpdate.length > 0) return true;
    const filteredAccounts = (form || []).filter(acc => {
      const bankName = (acc.bankName || '').trim();
      return bankName === '';
    });
    if (filteredAccounts.length > 0) {
      setAccountsToUpdate(filteredAccounts);
      return true;
    }
    return false;
  };

  const selectModal = () => {
    return shouldUpdateBankNames()
      ? () => setUpdateNames(true) // Needs to update bank names before continuing, update names dialog
      : enabled
      ? () => setConfirm(true) // All data seems good to go, confirmation dialog
      : save; // Direct to saving, no dialog shown
  };

  const save = () => {
    const primary = form.find(a => a.primary);
    const splitAccounts = form.filter(a => !a.primary);
    const splitType = amountType === '%' ? 1 : 2; // defined by FMS
    const formData = [];
    if (multiple) {
      const depositsToDelete = directDeposits.filter(a => a.id);
      if (depositsToDelete.length > 0) {
        depositsToDelete.forEach(deposit => {
          formData.push({
            ...formatDeposit(deposit),
            isDirectDeposit: false,
          });
        });
      }
    }
    if (multiple && splitAccounts.length > 0) {
      splitAccounts.forEach((deposit, i) =>
        formData.push(
          formatDeposit({ ...deposit, splitType, sequence: i + 1, id: null }),
        ),
      );
    }
    if (primary) {
      const primaryDeposit = formatDeposit(primary);
      primaryDeposit.id = !multiple ? defaultData.id : null;
      if (multiple) {
        primaryDeposit.sequence = form.length;
        primaryDeposit.splitType = splitType;
      }
      formData.push(primaryDeposit);
    }
    onSave(
      enabled
        ? {
            plaid: true,
            basicInfo: { dbCode },
            directDeposits: formData || null,
          }
        : {
            plaid: true,
            basicInfo: { dbCode },
            directDeposits: multiple
              ? directDeposits
                  .filter(a => a.id)
                  .map(account => ({
                    ...formatDeposit(account),
                    isDirectDeposit: enabled,
                  }))
              : [
                  {
                    ...formatDeposit(defaultData),
                    isDirectDeposit: enabled,
                  },
                ],
          },
    );
  };

  const message = (
    <Typography variant="body2" className={enabled && classes.enabled}>
      Direct Deposit is <strong>{enabled ? 'ON.' : 'OFF'}</strong>
      {!enabled &&
        `. You will receive paper ${isCanada ? 'cheques' : 'checks'}.`}
    </Typography>
  );

  return (
    <>
      {confirm && (
        <ConfirmModal
          form={form}
          confirm={confirm}
          setConfirm={setConfirm}
          save={save}
          mappings={mappings}
          amountType={amountType}
          paperless={paperless}
          split={multiple}
          removeRestriction={removeRestriction}
          isCurrencyEnabled={isCurrencyEnabled}
        />
      )}
      {updateNames && (
        <UpdateNamesModal
          form={form}
          setForm={setForm}
          accountsToUpdate={accountsToUpdate}
          showDialog={updateNames}
          setShowDialog={setUpdateNames}
          setConfirm={setConfirm}
          save={save}
          mappings={mappings}
        />
      )}
      <DeleteModal
        show={removing !== null}
        type="account"
        onClose={() => setRemoving(null)}
        onDelete={() => handleRemove(removing)}
      />
      <Grid container spacing={2} id="DirectDeposit-EditForm">
        <Grid item xs={12}>
          <Switch
            checked={enabled}
            onChange={e => setEnabled(e.target.checked)}
          >
            {message}
          </Switch>
        </Grid>
        {enabled && (
          <>
            <Grid item xs={12} sm={8}>
              <Typography variant="body2" size="short">
                Click "Connect to a bank" below to begin. When prompted, enter
                the login credentials you use to access your account on your
                selected bank's official website.
                <span className={classes.bold}>
                  Plaid* will never ask for your debit/bank card PIN.{' '}
                </span>
                Learn more in our{' '}
                <Link
                  target="_blank"
                  rel="noreferrer"
                  href={
                    isCanada
                      ? 'https://support.castandcrew.com/kb/mycastcrew-canada-signing-up-for-direct-deposit-desktop-mobile'
                      : 'https://support.castandcrew.com/kb/how-do-i-sign-up-for-direct-deposit'
                  }
                >
                  Help Center.
                </Link>
                <br />
                <br />
                {`Note that direct deposit may not be available for all payments.  When unavailable, a paper ${
                  isCanada ? `cheque` : `check`
                } is issued.`}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <ContentBox>
                <div className={classes.plaidBox}>
                  <img src={shield} alt="Shield Icon" />
                  <Typography variant="h6" className={classes.plaidTitle}>
                    Why Plaid?
                  </Typography>
                </div>
                <Typography variant="caption" style={{ marginBottom: 0 }}>
                  {isCanada
                    ? `Plaid enables us to securely link your bank account in order
                  to set up your direct deposit. Plaid does not retain any
                  banking information and uses your existing online banking
                  credentials to securely verify your bank account during setup.`
                    : `Plaid enables us to securely link your bank account in order to set up your direct deposit information. Plaid does not retain any banking information and uses your existing online banking credentials or two same-day mircodeposits to securely verify your bank account when setting up your direct deposit. `}
                  Learn more about why Plaid is safer than traditional methods{' '}
                  <Link
                    target="_blank"
                    rel="noreferrer"
                    href="https://support.castandcrew.com/kb/what-is-plaid"
                  >
                    here.
                  </Link>
                </Typography>
              </ContentBox>
            </Grid>
          </>
        )}
        {enabled && (
          <Grid item xs={12}>
            <BankAccounts
              accounts={form}
              maxAccounts={MAX_ACCOUNTS}
              updateAccounts={addBankAccounts}
              multiple={multiple}
              oAuthState={oAuthState}
              isCurrencyEnabled={isCurrencyEnabled}
            />
          </Grid>
        )}
        {enabled && (
          <Grid item xs={12}>
            <Grid container spacing={2} key={key('Table')}>
              <Grid item xs={12} style={{ paddingBottom: 0 }}>
                <Typography
                  variant="h5"
                  style={{ marginBottom: 0, marginTop: 16 }}
                >
                  Active direct deposit accounts
                </Typography>
              </Grid>
              <Grid item xs={12} style={{ overflowX: 'auto' }}>
                <Grid container spacing={0}>
                  {multiple && (
                    <>
                      <Grid item xs={12} md={7} xl={9}>
                        <Typography
                          variant="body1"
                          size="short"
                          className={classes.description}
                        >
                          Click and drag a row to rearrange its priority. The
                          last account listed automatically receives the
                          remaining balance after the higher priority accounts
                          and their splits are fulfilled first. Add up to 3
                          deposit accounts.
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={5}
                        xl={3}
                        style={{ display: 'flex' }}
                      >
                        <Grid
                          container
                          spacing={2}
                          justify="flex-end"
                          alignItems="center"
                        >
                          <Grid item style={{ paddingBottom: 0 }}>
                            <Typography variant="body1" size="short">
                              Split By:
                            </Typography>
                          </Grid>
                          <Grid item style={{ paddingBottom: 0 }}>
                            <Radio
                              disabled={form.length === 0}
                              checked={amountType === '%'}
                              onClick={() => setAmountType('%')}
                            >
                              Percent (%)
                            </Radio>
                            <Radio
                              disabled={form.length === 0}
                              checked={amountType === '$'}
                              onClick={() => setAmountType('$')}
                            >
                              Dollar ($)
                            </Radio>
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <AccountsTable
                      items={form}
                      onKey={key}
                      onChange={handleChange}
                      onRemove={setRemoving}
                      setItems={setForm}
                      multiple={multiple}
                      mappings={mappings}
                      amountType={amountType}
                      error={getSumPercentages() >= MAX_PERCENTAGE}
                      isCurrencyEnabled={isCurrencyEnabled}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container justify="flex-end">
                      <Grid item>
                        <Typography variant="caption" className={classes.error}>
                          {errorMessage()}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
        {enabled && !isCanada && (
          <Grid item xs={12} style={{ paddingTop: 0, paddingBottom: 0 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="h5"
                  style={{ marginBottom: 0, marginTop: 16 }}
                >
                  Paperless Option
                </Typography>
              </Grid>
              <Grid item style={{ paddingTop: 0, paddingLeft: 8 }}>
                <Checkbox
                  checked={paperless}
                  onChange={evt => {
                    setPaperless(evt.target.checked);
                    setDelivery(!evt.target.checked);
                  }}
                >
                  Go paperless to view your pay stubs online.
                </Checkbox>
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid item xs={12} md={7}>
          <Typography variant="caption" className={classes.finePrint}>
            * Visit <Link href="https://plaid.com">Plaid.com</Link> to learn
            more. Use of Plaid is subject to Plaid's{' '}
            <Link
              target="_blank"
              rel="noreferrer"
              href="https://www.plaid.com/privacy"
            >
              privacy policy.
            </Link>{' '}
            Updates on this page only affect employee personal records.
            Corporations can call Employee Help Desk at{' '}
            <Link href="tel:+18885704650">888.570.4650</Link> for assistance, or
            for Media Services call{' '}
            <Link href="tel:+13102541702">310.254.1702</Link>
          </Typography>
        </Grid>
        <Grid item xs={12} md={5}>
          <Grid
            container
            spacing={2}
            justify="flex-end"
            className={classes.actions}
          >
            <Grid item>
              <Button
                secondary
                width={153}
                onClick={onCancel}
                id="DirectDeposit-CancelButton"
              >
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                width={153}
                disabled={!valid()}
                onClick={selectModal()}
                id="DirectDeposit-EditForm-ContinueButton"
              >
                {enabled ? 'Continue' : 'Save'}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default EditForm;
